/**
 * MySQLUsersPanel — list and manage server-side MySQL users for one connection.
 * Rendered as an inner tab from Browser.jsx (admin only).
 *
 * Slice 1: list / create / edit (rename + password / plugin) / drop.
 * Slice 2 will add a Grants column + per-DB checkbox editor.
 *
 * Props:
 *   connectionId, connectionName
 *   active   — true when this inner tab is visible
 */
import { createSignal, createResource, onMount, For, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import {
  listMySQLUsers, createMySQLUser, editMySQLUser, dropMySQLUser,
  getUserGrants, setUserGrants,
} from '../lib/mysqlusers.js';
import { toast } from '../lib/toast.js';

// Human hints per plugin; the OFFERED set comes from the server (caps-filtered
// per version — see listMySQLUsers), so the dropdown can never promise a plugin
// this server rejects (native is gone on MySQL 8.4+/9, caching_sha2 doesn't
// exist on MariaDB / old MySQL).
const PLUGIN_HINTS = {
  mysql_native_password: 'classic, works with every client',
  caching_sha2_password: 'MySQL 8+ default (needs TLS or RSA exchange)',
  mysql_old_password:    'pre-4.1 legacy',
};

export default function MySQLUsersPanel(props) {
  const [data, { refetch }] = createResource(
    () => props.connectionId,
    (id) => listMySQLUsers(id).catch((e) => {
      toast.error(`Load users: ${e.message}`);
      return { users: [], plugins: ['mysql_native_password'], defaultPlugin: 'mysql_native_password' };
    }),
  );
  const users = () => data()?.users;
  const plugins = () => (data()?.plugins?.length ? data().plugins : ['mysql_native_password']);
  const defaultPlugin = () => data()?.defaultPlugin || 'mysql_native_password';
  const [filter, setFilter] = createSignal('');
  const [dialog, setDialog] = createSignal(null); // null | { mode:'new'|'edit', user, host, ... }
  const [grantTarget, setGrantTarget] = createSignal(null); // null | { user, host }

  const visibleUsers = () => {
    const f = filter().trim().toLowerCase();
    const arr = users() || [];
    if (!f) return arr;
    return arr.filter((u) =>
      u.user.toLowerCase().includes(f) || u.host.toLowerCase().includes(f));
  };

  const openNew = () => setDialog({
    mode: 'new',
    user: '', host: '%', plugin: defaultPlugin(), password: '',
  });
  const openEdit = (u) => setDialog({
    mode: 'edit',
    origUser: u.user, origHost: u.host,
    user: u.user, host: u.host,
    plugin: u.plugin || defaultPlugin(),
    password: '',
  });
  const closeDialog = () => setDialog(null);

  const save = async () => {
    const d = dialog();
    if (!d) return;
    if (!d.user.trim()) { toast.error('User is required.'); return; }
    try {
      if (d.mode === 'new') {
        if (!d.password) {
          if (!confirm('Create user with an empty password? This is rarely what you want.')) return;
        }
        await createMySQLUser(props.connectionId, {
          user: d.user.trim(), host: (d.host || '%').trim(),
          plugin: d.plugin, password: d.password,
        });
        toast.success(`User created: ${d.user}@${d.host || '%'}`);
      } else {
        const patch = {};
        if (d.user !== d.origUser) patch.newUser = d.user.trim();
        if (d.host !== d.origHost) patch.newHost = d.host.trim();
        if (d.password)            { patch.password = d.password; patch.plugin = d.plugin; }
        else if (d.plugin !== '')  { /* plugin-only change isn't supported by ALTER USER without
                                        re-specifying the password — skip silently */ }
        if (Object.keys(patch).length === 0) { toast.info('No changes.'); closeDialog(); return; }
        await editMySQLUser(props.connectionId, d.origUser, d.origHost, patch);
        toast.success('User updated');
      }
      closeDialog();
      refetch();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (u) => {
    if (!confirm(`DROP USER ${u.user}@${u.host}?\n\nAll grants for this account will be removed. This cannot be undone.`)) return;
    try {
      await dropMySQLUser(props.connectionId, u.user, u.host);
      toast.success('User dropped');
      refetch();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div class="mysqlusers-panel">
      <div class="browser-breadcrumb-row">
        <div class="breadcrumb mono">
          <span class="muted">{props.connectionName}</span>
          <span class="sep">/</span>
          <span class="strong">MySQL users</span>
        </div>
      </div>

      <div class="browser-toolbar">
        <input class="input objects-filter-input" style="max-width:280px;"
               placeholder="Filter…"
               value={filter()}
               onInput={(e) => setFilter(e.target.value)} />
        <button class="btn btn-sm" onClick={openNew}>+ New user</button>
        <button class="btn btn-sm btn-ghost" onClick={() => refetch()}
                disabled={data.loading}>⟳ Refresh</button>
        <div class="spacer" />
        <span class="muted mono" style="font-size:11px;">
          {visibleUsers().length} of {(users() || []).length}
        </span>
      </div>

      <div class="mysqlusers-list">
        <div class="mysqlusers-head">
          <span>User</span>
          <span>Host</span>
          <span>Plugin</span>
          <span>Status</span>
          <span style="text-align:right;">Actions</span>
        </div>
        <Show when={!data.loading} fallback={
          <div class="muted" style="padding:16px;">Loading users…</div>
        }>
          <For each={visibleUsers()}>
            {(u) => (
              <div class="mysqlusers-row">
                <span class="mono strong">{u.user || <span class="muted">(empty)</span>}</span>
                <span class="mono">{u.host}</span>
                <span class="mono muted">{u.plugin}</span>
                <span class="mono">
                  <Show when={u.accountLocked}>
                    <span class="badge readonly" style="margin-right:4px;">locked</span>
                  </Show>
                  <Show when={u.passwordExpired}>
                    <span class="badge readonly">pw expired</span>
                  </Show>
                </span>
                <span style="display:flex;gap:6px;justify-content:flex-end;">
                  <button class="btn-link" onClick={() => openEdit(u)}>edit</button>
                  <button class="btn-link" onClick={() => setGrantTarget({ user: u.user, host: u.host })}>grants</button>
                  <button class="btn-link btn-link-danger" onClick={() => remove(u)}>drop</button>
                </span>
              </div>
            )}
          </For>
          <Show when={visibleUsers().length === 0}>
            <div class="muted" style="padding:16px;">No users match.</div>
          </Show>
        </Show>
      </div>

      <Show when={dialog()}>
        <div class="modal-backdrop" onMouseDown={closeDialog}>
          <div class="modal-card" onMouseDown={(e) => e.stopPropagation()}>
            <h3>{dialog().mode === 'new' ? 'Create MySQL user' : `Edit ${dialog().origUser}@${dialog().origHost}`}</h3>

            <div class="mysqlusers-form">
              <label class="muted">User</label>
              <input class="input mono" autofocus
                     value={dialog().user}
                     placeholder="alice"
                     onInput={(e) => setDialog({ ...dialog(), user: e.target.value })} />

              <label class="muted">Host</label>
              <input class="input mono"
                     value={dialog().host}
                     placeholder="% for any host, or 192.168.1.%, localhost, …"
                     onInput={(e) => setDialog({ ...dialog(), host: e.target.value })} />

              <label class="muted">Auth plugin</label>
              <select class="input"
                      value={dialog().plugin}
                      onChange={(e) => setDialog({ ...dialog(), plugin: e.target.value })}>
                <For each={plugins()}>{(p) =>
                  <option value={p} selected={dialog().plugin === p}>
                    {p}{p === defaultPlugin() ? ' (default)' : ''}{PLUGIN_HINTS[p] ? ` — ${PLUGIN_HINTS[p]}` : ''}
                  </option>
                }</For>
              </select>

              <label class="muted">
                {dialog().mode === 'new' ? 'Password' : 'New password'}
              </label>
              <input class="input mono"
                     type="text"
                     value={dialog().password}
                     placeholder={dialog().mode === 'edit' ? '(leave blank to keep current)' : ''}
                     onInput={(e) => setDialog({ ...dialog(), password: e.target.value })} />
            </div>

            <Show when={dialog().mode === 'edit' && dialog().plugin === 'mysql_old_password'}>
              <p class="muted" style="font-size:11.5px;margin-top:10px;color:var(--warning,#fbbf24);">
                Note: mysql_old_password was removed in MySQL 8. The server will reject this if it's running 8.0+.
              </p>
            </Show>

            <div style="display:flex;gap:8px;margin-top:14px;justify-content:flex-end;">
              <button class="btn btn-ghost" onClick={closeDialog}>Cancel</button>
              <button class="btn" onClick={save}>
                {dialog().mode === 'new' ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </Show>

      <Show when={grantTarget()}>
        <GrantEditorModal
          connectionId={props.connectionId}
          user={grantTarget().user}
          host={grantTarget().host}
          onClose={() => setGrantTarget(null)}
        />
      </Show>
    </div>
  );
}

// PrivChecklist — one scope's privilege grid: a "Check all" master toggle over
// the individual privilege checkboxes. `privs` is a { PRIV: bool } store slice
// and `catalog` the (already caps-filtered) privileges the server offers. The
// parent owns the store path via onToggleOne(priv, val) / onToggleAll(patch),
// where patch is a { PRIV: val } object setStore shallow-merges at the leaf.
function PrivChecklist(props) {
  const allChecked = () => props.catalog.length > 0 && props.catalog.every((p) => !!props.privs[p]);
  const someChecked = () => props.catalog.some((p) => !!props.privs[p]);
  const buildAll = (val) => props.catalog.reduce((m, p) => { m[p] = val; return m; }, {});
  return (
    <div class="grant-privs">
      <label class="grant-priv grant-priv-all">
        <input type="checkbox"
               checked={allChecked()}
               prop:indeterminate={!allChecked() && someChecked()}
               onChange={(e) => props.onToggleAll(buildAll(e.target.checked))} />
        <strong>Check all</strong>
      </label>
      <For each={props.catalog}>{(p) =>
        <label class="grant-priv">
          <input type="checkbox" checked={!!props.privs[p]}
                 onChange={(e) => props.onToggleOne(p, e.target.checked)} />
          {p}
        </label>
      }</For>
    </div>
  );
}

// GrantEditorModal — privilege-checkbox editor for one user's global (*.*) and
// per-database (`db`.*) privileges. Loads the current grants, lets the admin
// toggle privileges + WITH GRANT OPTION per scope, and PUTs the desired set;
// the server diffs it into GRANT / REVOKE statements.
function GrantEditorModal(props) {
  // dbs is an ARRAY ({ db, privs, grantOption }) rather than a map so Solid's
  // <For> reacts cleanly to add/remove. privs is a { PRIV: bool } lookup.
  const [s, setS] = createStore({
    loading: true,
    saving: false,
    catalog: { global: [], database: [] },
    allDatabases: [],
    global: { privs: {}, grantOption: false },
    dbs: [],
    newDb: '',
  });

  const toMap = (arr) => (arr || []).reduce((m, p) => { m[p] = true; return m; }, {});
  const collect = (privs) => Object.keys(privs).filter((p) => privs[p]);

  onMount(async () => {
    try {
      const r = await getUserGrants(props.connectionId, props.user, props.host);
      const g = r.grants || { global: { privileges: [], grantOption: false }, databases: {} };
      const dbs = Object.entries(g.databases || {}).map(([db, e]) => ({
        db, privs: toMap(e.privileges), grantOption: !!e.grantOption,
      }));
      setS({
        loading: false,
        catalog: r.catalog || { global: [], database: [] },
        allDatabases: r.databases || [],
        global: { privs: toMap(g.global.privileges), grantOption: !!g.global.grantOption },
        dbs,
      });
    } catch (e) {
      toast.error('Load grants: ' + e.message);
      props.onClose();
    }
  });

  const availableDbs = () => s.allDatabases.filter((d) => !s.dbs.some((x) => x.db === d));
  const addDb = () => {
    if (!s.newDb || s.dbs.some((x) => x.db === s.newDb)) return;
    setS('dbs', (arr) => [...arr, { db: s.newDb, privs: {}, grantOption: false }]);
    setS('newDb', '');
  };
  const removeDb = (idx) => setS('dbs', (arr) => arr.filter((_, i) => i !== idx));

  const save = async () => {
    const desired = {
      global: { privileges: collect(s.global.privs), grantOption: s.global.grantOption },
      databases: {},
    };
    for (const e of s.dbs) {
      desired.databases[e.db] = { privileges: collect(e.privs), grantOption: e.grantOption };
    }
    setS('saving', true);
    try {
      await setUserGrants(props.connectionId, props.user, props.host, desired);
      toast.success('Grants updated');
      props.onClose();
    } catch (e) {
      toast.error(e.message);
      setS('saving', false);
    }
  };

  return (
    <div class="modal-backdrop" onMouseDown={props.onClose}>
      <div class="modal-card" style="width:760px;max-width:94vw;" onMouseDown={(e) => e.stopPropagation()}>
        <h3>Grants — <span class="mono">{props.user}@{props.host}</span></h3>
        <Show when={!s.loading} fallback={<div class="muted" style="padding:16px;">Loading grants…</div>}>
          <div class="grant-editor">
            {/* Global scope */}
            <div class="grant-scope">
              <div class="grant-scope-head">
                <span class="strong">Global</span>
                <span class="mono muted">*.*</span>
                <label class="grant-go">
                  <input type="checkbox" checked={s.global.grantOption}
                         onChange={(e) => setS('global', 'grantOption', e.target.checked)} />
                  WITH GRANT OPTION
                </label>
              </div>
              <PrivChecklist
                privs={s.global.privs} catalog={s.catalog.global}
                onToggleOne={(p, v) => setS('global', 'privs', p, v)}
                onToggleAll={(patch) => setS('global', 'privs', patch)} />
            </div>

            {/* Per-database scopes */}
            <For each={s.dbs}>{(entry, i) =>
              <div class="grant-scope">
                <div class="grant-scope-head">
                  <span class="strong mono">{entry.db}</span>
                  <span class="mono muted">`{entry.db}`.*</span>
                  <label class="grant-go">
                    <input type="checkbox" checked={entry.grantOption}
                           onChange={(e) => setS('dbs', i(), 'grantOption', e.target.checked)} />
                    WITH GRANT OPTION
                  </label>
                  <button class="btn-link btn-link-danger" onClick={() => removeDb(i())}>remove</button>
                </div>
                <PrivChecklist
                  privs={entry.privs} catalog={s.catalog.database}
                  onToggleOne={(p, v) => setS('dbs', i(), 'privs', p, v)}
                  onToggleAll={(patch) => setS('dbs', i(), 'privs', patch)} />
              </div>
            }</For>

            {/* Add a database scope */}
            <div class="grant-add-db">
              <select class="input" style="max-width:280px;" value={s.newDb}
                      onChange={(e) => setS('newDb', e.target.value)}>
                <option value="">+ Add database…</option>
                <For each={availableDbs()}>{(d) => <option value={d}>{d}</option>}</For>
              </select>
              <button class="btn btn-sm" onClick={addDb} disabled={!s.newDb}>Add</button>
            </div>
          </div>

          <div style="display:flex;gap:8px;margin-top:14px;justify-content:flex-end;">
            <button class="btn btn-ghost" onClick={props.onClose}>Cancel</button>
            <button class="btn" onClick={save} disabled={s.saving}>Save grants</button>
          </div>
        </Show>
      </div>
    </div>
  );
}
